import type { AiModelId, AiReasoningEffort, AiVerbosity } from './models';
import {
  AI_MAX_OUTPUT_TOKENS_MAX,
  AI_MAX_OUTPUT_TOKENS_MIN,
  AI_REASONING_EFFORTS,
  AI_MODELS,
  isAiModelId,
  isAiReasoningEffort,
  isAiVerbosity,
  isEffortSupported,
} from './models';

export interface AiModelSettings {
  model: AiModelId;
  effort: AiReasoningEffort;
  verbosity: AiVerbosity;
  maxOutputTokens: number;
}

export type AiModelSettingsResolution = { ok: true; settings: AiModelSettings } | { ok: false; message: string };

export function clampMaxOutputTokens(value: number): number {
  return Math.min(AI_MAX_OUTPUT_TOKENS_MAX, Math.max(AI_MAX_OUTPUT_TOKENS_MIN, Math.round(value)));
}

/** The supported effort nearest to `effort` on the shared scale, preferring the lower one on ties. */
export function closestSupportedEffort(model: AiModelId, effort: AiReasoningEffort): AiReasoningEffort {
  const supported = AI_MODELS[model].reasoningEfforts;
  if ((supported as readonly string[]).includes(effort)) return effort;

  const target = AI_REASONING_EFFORTS.indexOf(effort);
  return supported.reduce((best, candidate) =>
    Math.abs(AI_REASONING_EFFORTS.indexOf(candidate) - target) < Math.abs(AI_REASONING_EFFORTS.indexOf(best) - target) ? candidate : best,
  );
}

/**
 * Merges untrusted per-run overrides onto feature defaults. An explicitly requested effort the model
 * does not support is rejected; an inherited default effort is moved to the closest supported one.
 */
export function resolveAiModelSettings(defaults: AiModelSettings, overrides: unknown): AiModelSettingsResolution {
  if (overrides === undefined || overrides === null) {
    return { ok: true, settings: defaults };
  }
  if (typeof overrides !== 'object') {
    return { ok: false, message: 'settings must be an object' };
  }

  const { model, effort, verbosity, maxOutputTokens } = overrides as Record<string, unknown>;

  if (model !== undefined && !isAiModelId(model)) {
    return { ok: false, message: 'settings.model is not a supported model' };
  }
  if (effort !== undefined && !isAiReasoningEffort(effort)) {
    return { ok: false, message: 'settings.effort is not a supported value' };
  }
  if (verbosity !== undefined && !isAiVerbosity(verbosity)) {
    return { ok: false, message: 'settings.verbosity is not a supported value' };
  }
  if (maxOutputTokens !== undefined && (typeof maxOutputTokens !== 'number' || !Number.isFinite(maxOutputTokens))) {
    return { ok: false, message: 'settings.maxOutputTokens must be a number' };
  }

  const resolvedModel = model ?? defaults.model;

  if (effort !== undefined && !isEffortSupported(resolvedModel, effort)) {
    return { ok: false, message: `${resolvedModel} does not support reasoning effort "${effort}"` };
  }

  return {
    ok: true,
    settings: {
      model: resolvedModel,
      effort: effort ?? closestSupportedEffort(resolvedModel, defaults.effort),
      verbosity: verbosity ?? defaults.verbosity,
      maxOutputTokens: maxOutputTokens === undefined ? defaults.maxOutputTokens : clampMaxOutputTokens(maxOutputTokens),
    },
  };
}
